// ---------------------------------------------------------------------------
// Landing — the public marketing page at pleasantroutes.com (web only).
//
// The native app and returning users skip this entirely (see App.tsx routing:
// native → the app; web "/" → this page; the app lives at "/app"). This page
// exists to SELL a first-time visitor before they ever build a route: pose
// the question every map asks, grant it, turn — then show what Pleasant is.
//
// The hero animates: each line's letters appear left-to-right (as you read),
// spread wide then settling, slower so every beat lands. Respects
// prefers-reduced-motion — those visitors get the final state instantly.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Apple } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { EmailCapture } from "@/components/EmailCapture";
import { PleasantLogo } from "@/components/PleasantLogo";
import { APP_STORE_URL } from "@/content/appLinks";

/** A hero line whose letters reveal left-to-right, spread wide then settling. */
function HeroLine({
  text,
  startS,
  className,
  as: Tag = "p",
  pauseAfterWord,
  pauseS = 0,
  boldWords,
  perWord = 0.13,
}: {
  text: string;
  startS: number;
  className?: string;
  as?: "p" | "h1";
  /** Insert an extra pause AFTER this word index (e.g. after "decades,"),
   *  so "For decades," lands, beats, then the sentence continues. */
  pauseAfterWord?: number;
  pauseS?: number;
  /** Word indices to render bold (e.g. emphasize "pleasant routes"). */
  boldWords?: number[];
  /** Seconds between words. Raise it to make a line land more slowly. */
  perWord?: number;
}) {
  const ref = useRef<HTMLParagraphElement & HTMLHeadingElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return; // keep the static text already in the DOM

    // Word-by-word reveal: each word arrives with presence — coming into
    // focus (blur→sharp) and scaling up to size — rather than letters
    // floating in one by one (which read as ghostly "typing"). This feels
    // intentional and bold, and lands word by word the way you read.
    const PER = perWord; // seconds between words
    el.textContent = "";
    el.style.display = "block";

    const words = text.split(/(\s+)/); // keep whitespace chunks
    let wi = 0; // word index (skips whitespace)
    for (const chunk of words) {
      if (/^\s+$/.test(chunk)) {
        el.appendChild(document.createTextNode(chunk)); // breakable space → wraps
        continue;
      }
      const w = document.createElement("span");
      w.textContent = chunk;
      w.style.display = "inline-block";
      w.style.opacity = "0";
      w.style.animation = "pr-word 0.34s ease-out forwards";
      const extra = pauseAfterWord != null && wi > pauseAfterWord ? pauseS : 0;
      w.style.animationDelay = `${startS + wi * PER + extra}s`;
      if (boldWords?.includes(wi)) {
        w.style.fontWeight = "500";
        w.style.color = "hsl(var(--foreground))";
      }
      el.appendChild(w);
      wi++;
    }
  }, [text, startS, pauseAfterWord, pauseS, perWord]);

  return (
    <Tag ref={ref} className={className}>
      {text}
    </Tag>
  );
}

function Fade({ delayS, children, className, durationS = 0.7 }: { delayS: number; children: React.ReactNode; className?: string; durationS?: number }) {
  return (
    <div
      className={className}
      style={{ opacity: 0, animation: `pr-fade ${durationS}s ease ${delayS}s forwards` }}
    >
      {children}
    </div>
  );
}

const Landing = () => {
  // Tap anywhere on the hero to skip the intro animation and show everything.
  //
  // Declared ABOVE the shared-trip redirect below, not after it (where it sat
  // until Sep 25). Past the early return this was a rules-of-hooks violation:
  // a rescued visit rendered 0 hooks, an ordinary one 1. The redirect
  // unmounts almost immediately, so this was a latent trap rather than an
  // observed crash — but it is reachable the moment anything re-renders
  // Landing across a change in that query param.
  const [skip, setSkip] = useState(false);

  // SHARED-TRIP RESCUE (Morgan, July 17: "it just sends a link to the
  // pleasant home page - it's not the route itself"). Share links point at
  // "/", which renders this marketing page on the web — the trip params
  // were silently discarded. Any visit carrying an `end=` trip param is a
  // shared trip: hand it straight to the app with the query intact. Also
  // rescues every link already sent out.
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("end")) {
    return <Navigate to={`/app${window.location.search}`} replace />;
  }
  return (
    <main className="min-h-screen bg-hero text-foreground">
      <PageMeta
        title="Pleasant — navigation by joy, not just speed"
        description="Pleasant is a navigation app that finds the nicer route — past trees, water, and the places worth seeing — and tells you why it chose it."
        path="/"
      />
      <style>{`
        @keyframes pr-word { 0% { opacity:0; transform:translateY(2px);} 100% { opacity:1; transform:none;} }
        @keyframes pr-fade { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:none;} }
        @keyframes pr-draw { to { stroke-dashoffset:0; } }
        @keyframes pr-pop { 0%{opacity:0;transform:scale(0.4);} 60%{transform:scale(1.15);} 100%{opacity:1;transform:scale(1);} }
        @media (prefers-reduced-motion: reduce){
          [style*="pr-word"],[style*="pr-fade"],[style*="pr-draw"],[style*="pr-pop"]{opacity:1 !important;animation:none !important;stroke-dashoffset:0 !important;transform:none !important;filter:none !important;}
        }
        /* Tap-to-skip: clicking the hero fast-forwards every animation to its end. */
        .pr-skip [style*="pr-word"],.pr-skip [style*="pr-fade"],.pr-skip [style*="pr-draw"],.pr-skip [style*="pr-pop"]{opacity:1 !important;animation:none !important;stroke-dashoffset:0 !important;transform:none !important;filter:none !important;}
      `}</style>

      {/* Hero — tap anywhere to skip the intro animation */}
      <section
        onClick={() => setSkip(true)}
        className={`mx-auto w-full max-w-2xl px-6 pt-12 pb-12 text-center ${skip ? "pr-skip" : ""}`}
      >
        <PleasantLogo size="md" withPin className="mb-6 text-primary" />

        {/* Route-draws-itself illustration */}
        <svg viewBox="0 0 340 110" className="w-full max-w-md mx-auto mb-4" role="img" aria-label="A route drawing past trees and water to a destination">
          <path d="M20,90 C70,90 80,30 140,30 S230,90 300,38" fill="none" stroke="hsl(199 75% 41%)" strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray="430" strokeDashoffset="430" style={{ animation: "pr-draw 3.2s ease-out 0.3s forwards" }} />
          <circle cx="20" cy="90" r="6" fill="hsl(186 65% 44%)" style={{ opacity: 0, animation: "pr-pop 0.5s ease 0.2s forwards" }} />
          <g style={{ opacity: 0, animation: "pr-pop 0.5s ease 1.5s forwards", transformOrigin: "140px 30px" }}>
            <circle cx="140" cy="30" r="11" fill="hsl(173 55% 90%)" />
            <path d="M140,23 l5,9 h-10 z M139,32 h2 v4 h-2 z" fill="hsl(186 65% 32%)" />
          </g>
          <g style={{ opacity: 0, animation: "pr-pop 0.5s ease 2.4s forwards", transformOrigin: "228px 64px" }}>
            <circle cx="228" cy="64" r="11" fill="hsl(199 60% 90%)" />
            <path d="M228,58 C224,63 224,67 228,70 C232,67 232,63 228,58 Z" fill="hsl(199 75% 38%)" />
          </g>
          <g style={{ opacity: 0, animation: "pr-pop 0.6s ease 3.3s forwards", transformOrigin: "300px 38px" }}>
            <path d="M300,28 C295,28 292,32 292,36 C292,41 300,50 300,50 C300,50 308,41 308,36 C308,32 305,28 300,28 Z" fill="hsl(199 70% 16%)" />
            <circle cx="300" cy="36" r="3" fill="#fff" />
          </g>
        </svg>

        {/* Static headline — no animation (replaced the four-line "fastest
            question" intro). */}
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground leading-[1.12] mt-4 mb-6">
          A nicer way to where you're going
        </h1>

        <HeroLine
          startS={0.5}
          perWord={0.14}
          pauseAfterWord={11}
          pauseS={0.5}
          text="Pleasant is a navigation app that finds the most pleasant routes — not just the fastest."
          boldWords={[9, 10]}
          className="text-base sm:text-lg leading-relaxed text-foreground/80 max-w-md mx-auto mb-8"
        />

        {/* Pause, then the two buttons arrive one by one */}
        <div className="flex flex-col gap-3 max-w-xs mx-auto">
          <Fade delayS={1.2}>
            <a href="/app" className="block bg-pleasant text-primary-foreground text-[15px] font-medium py-3.5 rounded-full shadow-glow hover:brightness-110 transition-smooth">
              Open it in your browser
            </a>
          </Fade>
          <Fade delayS={1.4}>
            {/* Live on the App Store since July 2026 — this used to point at
                /beta (TestFlight instructions), now goes straight to the
                listing. */}
            <a
              href={APP_STORE_URL}
              className="flex items-center justify-center gap-2 border border-border text-foreground text-[15px] font-medium py-3.5 rounded-full hover:bg-muted/50 transition-smooth"
            >
              <Apple className="w-4 h-4" /> Get it on your iPhone
            </a>
          </Fade>
        </div>

        {/* Sign up for updates */}
        <Fade delayS={1.7} className="mt-12 max-w-xs mx-auto">
          <p className="text-sm font-medium text-foreground mb-3">Sign up for updates</p>
          <EmailCapture source="landing" />
        </Fade>

        {/* Quiet secondary link to the "why we built this" essay */}
        <Fade delayS={1.9} className="mt-8">
          <Link
            to="/overlooked"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth"
          >
            Why we built this <span aria-hidden="true">→</span>
          </Link>
        </Fade>
      </section>

      {/* Footer */}
      <footer className="mx-auto w-full max-w-2xl px-6 py-8 text-center text-xs text-muted-foreground">
        Made in the Hudson Valley
      </footer>
    </main>
  );
};

export default Landing;
